import { assertPrivateSessionLifetime, getPrivateSessionState, type PrivateSessionLifetime } from "@/lib/api/private-session";
import { changePrivateSession } from "@/shared/privateSessionChange";
import { openRestaurantNotification, type NotificationOpenDependencies } from "./notificationBehavior";

/** One owned cookie transition includes target inspection and original-binding repair. */
export async function openPrivateRestaurantNotification(dependencies: NotificationOpenDependencies, lifetime: PrivateSessionLifetime) {
  assertPrivateSessionLifetime(lifetime);
  const canSwitch = dependencies.notification.storeId !== dependencies.session.seller.storeId &&
    dependencies.session.availableStores?.some(store => store.storeId === dependencies.notification.storeId && store.storeKind === "RESTAURANT");
  if (!canSwitch) {
    const guard = () => assertPrivateSessionLifetime(lifetime);
    const result = await openRestaurantNotification({
      ...dependencies,
      navigate: route => { guard(); dependencies.navigate(route); },
      onUnavailable: () => { guard(); dependencies.onUnavailable(); },
      onFailure: () => { guard(); dependencies.onFailure(); },
    });
    guard(); return result;
  }
  let feedback: "unavailable" | "failed" | undefined;
  let recovery = false;
  const result = await changePrivateSession(dependencies.queryClient, async () => {
    const generation = getPrivateSessionState().generation;
    const guard = () => { if (getPrivateSessionState().generation !== generation) throw new DOMException("Session changed", "AbortError"); };
    return openRestaurantNotification({
      ...dependencies,
      switchStore: async slug => { guard(); await dependencies.switchStore(slug); guard(); },
      refreshSession: async () => { guard(); const session = await dependencies.refreshSession(); guard(); return session; },
      navigate: route => { guard(); dependencies.navigate(route); },
      recoverBinding: () => { guard(); recovery = true; },
      onUnavailable: () => { guard(); feedback = "unavailable"; },
      onFailure: () => { guard(); feedback = "failed"; },
    });
  });
  if (getPrivateSessionState().generation !== lifetime.generation + 1) throw new DOMException("Session changed", "AbortError");
  if (recovery) dependencies.recoverBinding();
  if (!getPrivateSessionState().changing) {
    if (feedback === "unavailable") dependencies.onUnavailable();
    if (feedback === "failed") dependencies.onFailure();
  }
  return result;
}
